import { makeAutoObservable, runInAction } from 'mobx';
import { inviteCodeApi } from '@/services/inviteCodeApi';
import { landingWaitlistApi } from '@/services/landingWaitlistApi';
import type {
  LandingWaitlistEntry as ApiLandingWaitlistEntry,
  LandingWaitlistAttribution,
  LandingWaitlistQuestionnaire,
} from '@/types';
import { brandStore } from './brandStore';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const INVITE_CODE_SCAN_PAGE_SIZE = 100;

export interface LandingWaitlistEntry {
  id: string;
  brandId: string;
  email: string;
  status: number;
  source: string;
  attribution?: LandingWaitlistAttribution;
  inviteCodeId?: number;
  inviteCode?: string;
  usedBy?: string;
  questionnaire?: LandingWaitlistQuestionnaire | null;
  createdAt: string;
  updatedAt: string;
}

function buildEntryId(item: ApiLandingWaitlistEntry) {
  return `${item.brand_id}:${item.email}:${item.created_at}`;
}

function readInviteCodeId(item: ApiLandingWaitlistEntry) {
  return item.inviteCodeId ?? item.invite_code_id;
}

function transformEntry(item: ApiLandingWaitlistEntry): LandingWaitlistEntry {
  return {
    id: buildEntryId(item),
    brandId: item.brand_id,
    email: item.email,
    status: item.status,
    source: item.source,
    attribution: item.attribution,
    inviteCodeId: readInviteCodeId(item),
    questionnaire: item.questionnaire,
    createdAt: item.created_at,
    updatedAt: item.updated_at,
  };
}

async function withInviteCodes(entries: LandingWaitlistEntry[], appId: string) {
  const inviteCodeIds = Array.from(
    new Set(
      entries
        .map((entry) => entry.inviteCodeId)
        .filter((id): id is number => typeof id === 'number' && id > 0),
    ),
  );
  if (inviteCodeIds.length === 0) {
    return entries;
  }

  const pairs = await Promise.all(
    inviteCodeIds.map(async (id) => {
      try {
        const response = await inviteCodeApi.getInviteCode({
          app_id: appId,
          id: String(id),
        });
        return [
          id,
          {
            code: response.invite_code.code,
            usedBy: response.invite_code.used_by,
          },
        ] as const;
      } catch (_error) {
        return [id, { code: '', usedBy: undefined }] as const;
      }
    }),
  );
  const inviteCodesById = new Map(pairs);
  return entries.map((entry) => ({
    ...entry,
    inviteCode: entry.inviteCodeId
      ? inviteCodesById.get(entry.inviteCodeId)?.code || undefined
      : undefined,
    usedBy: entry.inviteCodeId
      ? inviteCodesById.get(entry.inviteCodeId)?.usedBy
      : undefined,
  }));
}

class LandingWaitlistStore {
  entries: LandingWaitlistEntry[] = [];
  pagination = {
    page: 1,
    pageSize: 10,
    total: 0,
  };
  searchQuery = '';
  channelFilter = '';
  loading = false;
  sendingEmail = '';
  batchSending = false;

  constructor() {
    makeAutoObservable(this);
  }

  async fetchEntries() {
    const brandId = brandStore.requireActiveBrandAppId();
    const searchQuery = this.searchQuery.trim();
    this.loading = true;

    try {
      if (searchQuery) {
        await this.fetchSearchResult(brandId, searchQuery);
        return;
      }

      const response = await landingWaitlistApi.listAdmin(brandId, {
        page_num: this.pagination.page,
        page_size: this.pagination.pageSize,
        ...(this.channelFilter ? { channel: this.channelFilter } : {}),
      });
      const entries = await withInviteCodes(
        response.items.map(transformEntry),
        brandId,
      );

      runInAction(() => {
        this.entries = entries;
        this.pagination.total = response.total;
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.loading = false;
      });
      throw error;
    }
  }

  async sendInvite(entry: LandingWaitlistEntry) {
    const brandId = brandStore.requireActiveBrandAppId();
    this.sendingEmail = entry.email;

    try {
      const response = await landingWaitlistApi.sendInvite(brandId, {
        email: entry.email,
      });
      const updatedEntries = response.entry
        ? await withInviteCodes([transformEntry(response.entry)], brandId)
        : [];
      runInAction(() => {
        if (updatedEntries[0]) {
          const updatedEntry = updatedEntries[0];
          this.entries = this.entries.map((item) =>
            item.email === updatedEntry.email ? updatedEntry : item,
          );
        }
      });
      runInAction(() => {
        this.sendingEmail = '';
      });
    } catch (error) {
      runInAction(() => {
        this.sendingEmail = '';
      });
      throw error;
    }
  }

  async batchSendInvites(entries: LandingWaitlistEntry[]) {
    const brandId = brandStore.requireActiveBrandAppId();
    this.batchSending = true;
    try {
      const response = await landingWaitlistApi.batchSendInvites(brandId, {
        emails: entries.map((entry) => entry.email),
      });
      await this.fetchEntries();
      runInAction(() => {
        this.batchSending = false;
      });
      return response;
    } catch (error) {
      runInAction(() => {
        this.batchSending = false;
      });
      throw error;
    }
  }

  private async fetchSearchResult(brandId: string, searchQuery: string) {
    if (EMAIL_RE.test(searchQuery)) {
      const response = await landingWaitlistApi.check(brandId, {
        email: searchQuery,
      });
      const matchedEntry =
        response.entry &&
        (!this.channelFilter ||
          (this.channelFilter === 'unattributed'
            ? !response.entry.attribution?.channel
            : response.entry.attribution?.channel === this.channelFilter))
          ? response.entry
          : undefined;
      const entries = matchedEntry
        ? await withInviteCodes([transformEntry(matchedEntry)], brandId)
        : [];
      runInAction(() => {
        this.entries = entries;
        this.pagination.total = matchedEntry ? 1 : 0;
        this.loading = false;
      });
      return;
    }

    const inviteCodeId = await this.resolveInviteCodeId(searchQuery);
    if (inviteCodeId === null) {
      runInAction(() => {
        this.entries = [];
        this.pagination.total = 0;
        this.loading = false;
      });
      return;
    }

    const matchedEntries = await this.findEntriesByInviteCodeId(
      brandId,
      inviteCodeId,
    );
    const entries = await withInviteCodes(matchedEntries, brandId);
    runInAction(() => {
      this.entries = entries;
      this.pagination.total = entries.length;
      this.loading = false;
    });
  }

  private async resolveInviteCodeId(searchQuery: string) {
    const numericId = Number(searchQuery);
    if (Number.isInteger(numericId) && numericId > 0) {
      return numericId;
    }

    const appId = brandStore.activeBrandAppId.trim();
    if (!appId) {
      return null;
    }

    try {
      const response = await inviteCodeApi.getInviteCode({
        app_id: appId,
        code: searchQuery,
      });
      const parsedId = Number(response.invite_code.id);
      return Number.isInteger(parsedId) && parsedId > 0 ? parsedId : null;
    } catch (_error) {
      return null;
    }
  }

  private async findEntriesByInviteCodeId(
    brandId: string,
    inviteCodeId: number,
  ) {
    const matchedEntries: LandingWaitlistEntry[] = [];
    let pageNum = 1;
    let loaded = 0;
    let total = 0;

    do {
      const response = await landingWaitlistApi.listAdmin(brandId, {
        page_num: pageNum,
        page_size: INVITE_CODE_SCAN_PAGE_SIZE,
        ...(this.channelFilter ? { channel: this.channelFilter } : {}),
      });
      const items = response.items.map(transformEntry);
      if (items.length === 0) {
        break;
      }
      matchedEntries.push(
        ...items.filter((entry) => entry.inviteCodeId === inviteCodeId),
      );
      total = response.total || 0;
      loaded += response.items.length;
      pageNum += 1;
    } while (loaded < total);

    return matchedEntries;
  }

  setPagination(page: number, pageSize?: number) {
    this.pagination.page = page;
    if (pageSize !== undefined) {
      this.pagination.pageSize = pageSize;
    }
  }

  setSearchQuery(query: string) {
    this.searchQuery = query;
    this.pagination.page = 1;
  }

  setChannelFilter(channel: string) {
    this.channelFilter = channel;
    this.pagination.page = 1;
  }
}

export const landingWaitlistStore = new LandingWaitlistStore();
